import { Close as CloseIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon } from "@mui/icons-material";
import {
  CheckCircle,
  CircleOff,
  FileText,
  Globe,
  Hammer,
  Microscope,
  MousePointer,
  Puzzle,
  Settings,
  Share2,
  Users,
  Wrench,
} from "lucide-react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { INTEGRATION_PROVIDER_COMPOSIO } from "../../../constants";
import { FEATURES } from "../../../constants/features";
import { useFeatures } from "../../../context/FeatureContext";
import { hostname } from "../../../utils";
import { isFreeUser } from "../../../utils/subscriptionUtils";
import { useEmployeeTerminology } from "../../../utils/terminology";
import BrowseToolsBanner from "../../integrations/components/BrowseToolsBanner";
import ComposioCatalogDialog from "../../integrations/dialogs/ComposioCatalogDialog";
import ToolsetsManagementDialog from "../../integrations/dialogs/ToolsetsManagementDialog";

// MCP logo for MCP tools
const mcpLogo = require("../../../img/mcp.png");

interface ToolConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  settingKey: string;
  creditCost?: string;
  hasConfig?: boolean;
  beta?: boolean;
}

interface IntegrationTool {
  id: string;
  name: string;
  instanceName: string;
  type: string;
  status: string;
  integration_type: string;
  logo?: string;
  parameters?: {
    composioAppSlug?: string;
    composioConnectedAccountId?: string;
    logo?: string;
    description?: string;
    serverName?: string;
    transport?: string;
  };
}

interface BrowserTaskConfig {
  enabled: boolean;
  sessionType: string;
  integrationId: string | null;
}

interface ToolsDialogProps {
  open: boolean;
  onClose: () => void;
  contextSettings: any;
  setContextSettings: (settings: any) => void;
  currentModelSupportsTools: boolean;
  company: any;
  getToken?: () => Promise<string>;
  eventListener?: Function;
  currentPlan?: string;
}

export default function ToolsDialog({
  open,
  onClose,
  contextSettings,
  setContextSettings,
  currentModelSupportsTools,
  company,
  getToken,
  eventListener,
  currentPlan,
}: ToolsDialogProps) {
  const { isFeatureEnabled } = useFeatures();
  const { employees: employeesText, employeesLower } = useEmployeeTerminology();
  const integrationsFeatureEnabled = isFeatureEnabled(FEATURES.INTEGRATIONS);
  const audienceBuilderFeatureEnabled = isFeatureEnabled(FEATURES.AUDIENCE_BUILDER);
  const employeeFeatureEnabled = isFeatureEnabled(FEATURES.EMPLOYEE);
  const isPaidUser = !isFreeUser(currentPlan);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [composioCatalogOpen, setComposioCatalogOpen] = useState(false);
  const [toolsetsDialogOpen, setToolsetsDialogOpen] = useState(false);
  const [browserIntegrations, setBrowserIntegrations] = useState<any[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [integrationTools, setIntegrationTools] = useState<IntegrationTool[]>(
    []
  );
  const [loadingIntegrationTools, setLoadingIntegrationTools] = useState(false);

  // Fetch integration tools (MCP + Composio) when dialog opens
  useEffect(() => {
    if (open && getToken && company && integrationsFeatureEnabled) {
      const fetchIntegrationTools = async () => {
        setLoadingIntegrationTools(true);
        try {
          const token = await getToken();
          // Fetch MCP (tools), Composio (by provider), and Toolsets in one request
          const response = await axios.get(
            `${hostname}/integrations/by-type/tools,toolset`,
            {
              params: { companyId: company.id, provider: INTEGRATION_PROVIDER_COMPOSIO },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const items = response.data.items || [];
          // Filter to only show healthy integrations (or those without a status, which defaults to healthy)
          setIntegrationTools(items.filter((i: IntegrationTool) => !i.status || i.status === "healthy"));
        } catch (error) {
          console.error("Error fetching integration tools:", error);
        } finally {
          setLoadingIntegrationTools(false);
        }
      };
      fetchIntegrationTools();
    }
  }, [open, getToken, company, integrationsFeatureEnabled]);

  // Fetch browser session integrations when browser task tool is selected
  useEffect(() => {
    if (selectedItem === "browserTask" && getToken && company) {
      const fetchIntegrations = async () => {
        setLoadingIntegrations(true);
        try {
          const token = await getToken();
          const response = await axios.get(
            `${hostname}/integrations/by-type/browser_session`,
            {
              params: { companyId: company.id },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setBrowserIntegrations(response.data.items || []);
        } catch (error) {
          console.error("Error fetching integrations:", error);
        } finally {
          setLoadingIntegrations(false);
        }
      };
      fetchIntegrations();
    }
  }, [selectedItem, getToken, company]);

  // Tools configuration
  const tools: ToolConfig[] = [
    {
      id: "platformTools",
      name: "Enable platform API tools",
      description:
        "Allow the assistant to manage lists, sequences, agents, and other platform resources via API",
      icon: <Wrench size={28} />,
      settingKey: "platformTools",
    },
    ...(isPaidUser
      ? [
          {
            id: "researchTask",
            name: "Research Task",
            description:
              "AI can delegate complex research tasks to a specialized research agent",
            icon: <Microscope size={28} />,
            settingKey: "researchTask",
            creditCost: "+2 Credits",
          },
        ]
      : []),
    {
      id: "documentCreation",
      name: "Create Documents",
      description:
        "AI can generate PDF, DOCX, or CSV files and email them to you",
      icon: <FileText size={28} />,
      settingKey: "documentCreation",
    },
    ...(integrationsFeatureEnabled
      ? [
          {
            id: "browserTask",
            name: "Browser Automation",
            description:
              "Advanced browser automation for web scraping, form filling, and navigating websites",
            icon: <MousePointer size={28} />,
            settingKey: "browserTask",
            hasConfig: true,
          },
        ]
      : []),
    ...(audienceBuilderFeatureEnabled
      ? [
          {
            id: "audienceTools",
            name: "Audience Enrichment",
            description:
              "Enrich person or company data from the audience database with contact info, demographics, and social profiles",
            icon: <Users size={28} />,
            settingKey: "audienceTools",
            beta: true,
          },
        ]
      : []),
    ...(employeeFeatureEnabled
      ? [
          {
            id: "delegateTask",
            name: "Delegate Task",
            description: `AI can delegate subtasks to other ${employeesText} in your company, using their specialized skills and tools`,
            icon: <Share2 size={28} />,
            settingKey: "delegateTask",
            beta: true,
            creditCost: "+Credits",
          },
        ]
      : []),
  ];

  const isToolEnabled = (tool: ToolConfig): boolean => {
    if (tool.id === "browserTask") {
      return contextSettings.browserTask?.enabled || false;
    }
    return contextSettings[tool.settingKey] || false;
  };

  const isIntegrationToolEnabled = (tool: IntegrationTool): boolean => {
    if (tool.integration_type === "tools") {
      // MCP tools are stored in mcpServers
      return (contextSettings.mcpServers || []).includes(tool.id);
    }
    if (tool.type === "toolset") {
      // Toolsets are stored in toolsets
      return (contextSettings.toolsets || []).includes(tool.id);
    }
    // Composio tools are stored in composioTools
    return (contextSettings.composioTools || []).includes(tool.id);
  };

  const handleToolToggle = (tool: ToolConfig, enabled: boolean) => {
    if (tool.id === "browserTask") {
      setContextSettings({
        ...contextSettings,
        browserTask: {
          enabled,
          sessionType: enabled
            ? contextSettings.browserTask?.sessionType || "none"
            : "none",
          integrationId: enabled
            ? contextSettings.browserTask?.integrationId
            : null,
        },
        ...(enabled ? { strategy: "function-chain" } : {}),
      });
    } else {
      setContextSettings({
        ...contextSettings,
        [tool.settingKey]: enabled,
        ...(enabled ? { strategy: "function-chain" } : {}),
      });
    }
  };

  const handleIntegrationToolToggle = (tool: IntegrationTool, enabled: boolean) => {
    if (tool.integration_type === "tools") {
      // MCP tools are stored in mcpServers
      const currentServers = contextSettings.mcpServers || [];
      const newServers = enabled
        ? [...currentServers, tool.id]
        : currentServers.filter((id: string) => id !== tool.id);

      setContextSettings({
        ...contextSettings,
        mcpServers: newServers,
        ...(newServers.length > 0 ? { strategy: "function-chain" } : {}),
      });
    } else if (tool.type === "toolset") {
      // Toolsets are stored in toolsets
      const currentToolsets = contextSettings.toolsets || [];
      const newToolsets = enabled
        ? [...currentToolsets, tool.id]
        : currentToolsets.filter((id: string) => id !== tool.id);

      setContextSettings({
        ...contextSettings,
        toolsets: newToolsets,
        ...(newToolsets.length > 0 ? { strategy: "function-chain" } : {}),
      });
    } else {
      // Composio tools are stored in composioTools
      const currentTools = contextSettings.composioTools || [];
      const newTools = enabled
        ? [...currentTools, tool.id]
        : currentTools.filter((id: string) => id !== tool.id);

      setContextSettings({
        ...contextSettings,
        composioTools: newTools,
        ...(newTools.length > 0 ? { strategy: "function-chain" } : {}),
      });
    }
  };

  const handleBrowserTaskChange = (updates: Partial<BrowserTaskConfig>) => {
    setContextSettings({
      ...contextSettings,
      browserTask: {
        ...contextSettings.browserTask,
        ...updates,
      },
    });
  };

  const handleBack = () => {
    setSelectedItem(null);
  };

  const handleClose = () => {
    setSelectedItem(null);
    onClose();
  };

  const renderAllToolsGrid = () => {
    const isDisabled = !currentModelSupportsTools;

    return (
      <Grid container spacing={2}>
        {/* Built-in tools */}
        {tools.map((tool) => {
          const enabled = isToolEnabled(tool);
          return (
            <Grid item xs={12} sm={6} md={4} key={tool.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  height: 160,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  opacity: isDisabled ? 0.5 : 1,
                  borderColor: enabled ? "primary.main" : undefined,
                  "&:hover": !isDisabled
                    ? {
                        borderColor: "primary.main",
                        boxShadow: 1,
                      }
                    : {},
                }}
                onClick={() => {
                  if (!isDisabled) {
                    handleToolToggle(tool, !enabled);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {tool.icon}
                  </Avatar>
                  <Box sx={{ flex: 1, overflow: "hidden", minHeight: 80 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, flexWrap: "wrap" }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.3,
                        }}
                      >
                        {tool.name}
                      </Typography>
                      {tool.beta && (
                        <Chip
                          label="Beta"
                          size="small"
                          sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "action.hover", color: "text.secondary" }}
                        />
                      )}
                      {tool.hasConfig && (
                        <Settings
                          size={16}
                          style={{
                            color: "inherit",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                              setSelectedItem(tool.id);
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mt: 0.5,
                      }}
                    >
                      {tool.description}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  {tool.creditCost ? (
                    <Chip label={tool.creditCost} size="small" />
                  ) : (
                    <Box />
                  )}
                  {enabled ? (
                    <CheckCircle size={24} />
                  ) : (
                    <RadioButtonUncheckedIcon color="action" />
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}

        {/* Integration tools (MCP + Composio + Toolsets) */}
        {integrationTools.map((integration) => {
          const enabled = isIntegrationToolEnabled(integration);
          const isToolset = integration.type === "toolset";
          // Logo: use MCP logo for MCP tools, folder icon for toolsets, otherwise use parameters.logo (backend generates URL)
          const logoUrl = integration.integration_type === "tools"
            ? mcpLogo
            : isToolset
              ? null // Will use FolderIcon
              : integration.parameters?.logo || (integration as any).logo;
          // For MCP tools, use serverName as display name if available
          const displayName = integration.integration_type === "tools"
            ? integration.parameters?.serverName || integration.name
            : integration.instanceName || integration.name;
          const instanceLabel =
            integration.instanceName !== integration.name
              ? integration.instanceName
              : null;
          // Description based on tool type - use stored description if available
          const toolsetInfo = isToolset && integration.parameters
            ? `${(integration.parameters as any).toolCount || 0} tools from ${(integration.parameters as any).integrationCount || 0} integrations`
            : null;
          const description = integration.parameters?.description
            || (integration.integration_type === "tools" ? "MCP Server" : null)
            || toolsetInfo
            || instanceLabel
            || `Perform actions on ${displayName}`;

          return (
            <Grid item xs={12} sm={6} md={4} key={integration.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  height: 160,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  opacity: isDisabled ? 0.5 : 1,
                  borderColor: enabled ? "primary.main" : undefined,
                  "&:hover": !isDisabled
                    ? {
                        borderColor: "primary.main",
                        boxShadow: 1,
                      }
                    : {},
                }}
                onClick={() => {
                  if (!isDisabled) {
                    handleIntegrationToolToggle(integration, !enabled);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Avatar
                    src={logoUrl || undefined}
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      ...(isToolset
                        ? {
                            bgcolor: "background.paper",
                            color: "text.primary",
                            border: "1px solid",
                            borderColor: "divider",
                          }
                        : {
                            bgcolor: "action.disabledBackground",
                          }),
                    }}
                  >
                    {isToolset ? (
                      <Wrench size={28} />
                    ) : (
                      <Puzzle size={24} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Typography variant="subtitle1" noWrap>
                      {displayName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  {enabled ? (
                    <CheckCircle size={24} />
                  ) : (
                    <RadioButtonUncheckedIcon color="action" />
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderBrowserTaskConfig = () => {
    const browserTaskConfig = contextSettings.browserTask || {
      enabled: false,
      sessionType: "none",
      integrationId: null,
    };

    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: browserTaskConfig.enabled
                ? "primary.main"
                : "action.disabledBackground",
              width: 64,
              height: 64,
            }}
          >
            <MousePointer size={32} />
          </Avatar>
          <Box>
            <Typography variant="h6">Browser Automation</Typography>
            <Typography variant="body2" color="text.secondary">
              Advanced browser automation capabilities that allow the AI to
              control a web browser to perform complex tasks.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography>Enable Browser Tasks</Typography>
            <Switch
              checked={browserTaskConfig.enabled || false}
              onChange={(e) =>
                handleToolToggle(
                  {
                    id: "browserTask",
                    settingKey: "browserTask",
                  } as ToolConfig,
                  e.target.checked
                )
              }
              disabled={!currentModelSupportsTools}
            />
          </Box>
        </Box>

        {browserTaskConfig.enabled && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Session Type</InputLabel>
              <Select
                value={browserTaskConfig.sessionType || "none"}
                label="Session Type"
                onChange={(e) => {
                  const newSessionType = e.target.value;
                  handleBrowserTaskChange({
                    sessionType: newSessionType,
                    ...(newSessionType !== "integration"
                      ? { integrationId: null }
                      : {}),
                  });
                }}
              >
                <MenuItem value="none">No Session (Basic)</MenuItem>
                <MenuItem value="residential">Residential Proxy</MenuItem>
                <MenuItem value="integration">
                  Browser Session Integration
                </MenuItem>
              </Select>
            </FormControl>

            {browserTaskConfig.sessionType === "integration" && (
              <FormControl fullWidth disabled={loadingIntegrations}>
                <InputLabel>Browser Session Integration</InputLabel>
                <Select
                  value={browserTaskConfig.integrationId || ""}
                  label="Browser Session Integration"
                  onChange={(e) => {
                    handleBrowserTaskChange({ integrationId: e.target.value });
                  }}
                >
                  {browserIntegrations.length === 0 && !loadingIntegrations && (
                    <MenuItem value="" disabled>
                      No browser session integrations found
                    </MenuItem>
                  )}
                  {loadingIntegrations && (
                    <MenuItem value="" disabled>
                      Loading integrations...
                    </MenuItem>
                  )}
                  {browserIntegrations.map((integration) => (
                    <MenuItem key={integration.id} value={integration.id}>
                      {integration.instanceName ||
                        integration.name ||
                        "Unnamed"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}
      </Box>
    );
  };


  const refreshIntegrationTools = async () => {
    if (getToken && company && integrationsFeatureEnabled) {
      setLoadingIntegrationTools(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/tools,toolset`,
          {
            params: { companyId: company.id, provider: INTEGRATION_PROVIDER_COMPOSIO },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.data.items || [];
        setIntegrationTools(items.filter((i: IntegrationTool) => !i.status || i.status === "healthy"));
      } catch (error) {
        console.error("Error fetching integration tools:", error);
      } finally {
        setLoadingIntegrationTools(false);
      }
    }
  };

  return (
    <>
      <ToolsetsManagementDialog
        open={toolsetsDialogOpen}
        onClose={() => setToolsetsDialogOpen(false)}
        companyId={company?.id}
        getToken={getToken!}
        eventListener={eventListener || (() => {})}
      />
      <ComposioCatalogDialog
        open={composioCatalogOpen}
        onClose={() => setComposioCatalogOpen(false)}
        companyId={company?.id}
        getToken={getToken!}
        onSuccess={() => {
          refreshIntegrationTools();
          if (eventListener) {
            eventListener({
              type: "SET_SUCCESS",
              message: "Integration installed successfully",
            });
          }
        }}
        onError={(error) => {
          if (eventListener) {
            eventListener({ type: "SET_ERROR", error });
          }
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedItem ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="text"
              onClick={handleBack}
              sx={{ minWidth: "auto" }}
            >
              Back
            </Button>
            <Typography>/ Configure</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Hammer size={20} />
            <Typography variant="h6">Connected Tools</Typography>
          </Box>
        )}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!selectedItem && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Advanced AI capabilities that allow the assistant to perform
              actions beyond just answering questions.{" "}
              {!currentModelSupportsTools && (
                <strong>
                  Note: The current model doesn't support tools. Select a
                  tool-compatible model in settings.
                </strong>
              )}
            </Typography>
            {integrationsFeatureEnabled && (
              <>
                <BrowseToolsBanner onClick={() => setComposioCatalogOpen(true)} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setToolsetsDialogOpen(true)}
                  sx={{ mb: 2 }}
                >
                  Manage Toolsets
                </Button>
              </>
            )}
            {loadingIntegrationTools ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading tools...
                </Typography>
              </Box>
            ) : (
              renderAllToolsGrid()
            )}
          </>
        )}

        {selectedItem === "browserTask" && renderBrowserTaskConfig()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{selectedItem ? "Done" : "Close"}</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
